// Article spécial "Baromètre Sécurité Routière — Avril 2026 : ALERTE MOTARDS"
// Source officielle : sécurité-routière.gouv.fr (CP du baromètre mensuel d'avril 2026)
// Article pédagogique en réaction à la hausse alarmante de la mortalité 2RM.
// PDF source : https://www.securite-routiere.gouv.fr/sites/default/files/2026-05/cp-barometre-accidentalite-routiere-avril-2026.pdf

import {
  AlertTriangle,
  Bike,
  Users,
  TrendingUp,
  ShieldCheck,
  Gauge,
  Wine,
  Smartphone,
  Brain,
  Eye,
  Heart,
  Target,
  Calendar,
  Quote,
  ClipboardCheck,
  Lightbulb,
} from 'lucide-react';

export const barometreAvril2026Article = {
  slug: 'barometre-securite-routiere-avril-2026',
  weekVolume: 21,
  weekYear: 2026,
  monthLabel: 'Mai 2026',
  category: 'Actualité Sécurité Routière',
  shortTitle: 'Baromètre Avril 2026 — Alerte motards',
  title: 'Avril 2026 : +52 % de motards tués sur les routes',
  kicker: '🚨 Alerte officielle de la Sécurité Routière',
  subtitle:
    "Le baromètre national d'avril 2026 publié par la Sécurité Routière révèle une hausse dramatique de la mortalité des usagers de deux-roues motorisés : 79 motards tués en un seul mois, soit +27 vies fauchées par rapport à avril 2025. Décryptage pédagogique de chiffres qui doivent nous faire réfléchir — surtout toi, futur motard.",
  heroImage: '/images/articles/barometre-avril-2026-hero.jpg?v=20260514b',
  heroImageAlt:
    "Motard en milieu urbain au coucher du soleil — illustration du baromètre de la Sécurité Routière d'avril 2026 et de la reprise saisonnière de la circulation des deux-roues",
  topRightKicker: { line1: "Données officielles", line2: 'Avril 2026 · ONISR' },
  readTimeMin: 6,
  publishedAt: '2026-05-14',
  seoDescription:
    "Baromètre Sécurité Routière avril 2026 : 79 motards tués (+52 % vs 2025). Analyse pédagogique des causes et leçons concrètes pour les futurs motards. Source officielle ONISR.",
  seoKeywords:
    'baromètre sécurité routière avril 2026, mortalité motards 2026, accidents moto France, statistiques ONISR, prévention deux-roues motorisé, hausse mortalité moto, sécurité routière mai 2026',
  about:
    "Analyse du baromètre mensuel de la Sécurité Routière d'avril 2026 : hausse alarmante de la mortalité des deux-roues motorisés et leçons pédagogiques pour les apprenants moto.",
  source: {
    label: 'Sécurité routière — Communiqué officiel',
    url: 'https://www.securite-routiere.gouv.fr/actualites/barometre-de-laccidentalite-routiere-davril-2026',
    refs:
      "Baromètre mensuel ONISR — avril 2026. Bilan publié le 12 mai 2026 par la Délégation à la Sécurité Routière.",
  },
  chapo: {
    kicker: 'Pourquoi cet article te concerne directement',
    body:
      "Tu prépares ton permis moto ? Ces chiffres ne sont pas une statistique abstraite : ce sont <strong>79 motards comme toi</strong>, qui sont partis rouler un matin d'avril et ne sont jamais rentrés. <strong>+52 % en un an</strong> pour la seule catégorie deux-roues motorisé. Aucune autre catégorie d'usagers n'a connu une telle aggravation. Comprendre <em>pourquoi</em>, c'est déjà commencer à <em>se protéger</em>.",
    sideStat: {
      kicker: 'Le chiffre choc',
      big: '+52 %',
      body: "C'est la hausse de la mortalité <strong>deux-roues motorisé</strong> en avril 2026 vs avril 2025. 79 tués contre 52 — <strong>27 vies de plus</strong> en un seul mois.",
      accent: '#cf2031',
    },
  },
  pullQuote: {
    text:
      "La hausse très préoccupante du nombre de morts sur nos routes, notamment parmi les jeunes, est un drame humain que nous ne pouvons plus accepter. Derrière chaque chiffre, il y a des vies brisées, des familles endeuillées et des destins interrompus.",
    source: 'Marie-Pierre Vedrenne, ministre déléguée chargée de la Citoyenneté',
  },
  sections: [
    {
      part: 'Partie 1 — Les chiffres officiels',
      title: 'Avril 2026 : le mois le plus meurtrier depuis 5 ans',
      intro:
        "Voici les <strong>chiffres officiels</strong> publiés par la Sécurité Routière le 12 mai 2026. Tous proviennent de l'ONISR (Observatoire National Interministériel de la Sécurité Routière).",
      inlineImage: {
        src: '/images/articles/barometre-avril-2026-infographie.jpg?v=20260514',
        alt: "Infographie officielle de la Sécurité Routière — bilan d'accidentalité avril 2026 par mode de déplacement",
        caption:
          "Infographie officielle publiée par la Délégation à la Sécurité Routière. Détail des tués et blessés graves en avril 2026 par catégorie d'usagers.",
        credit: 'Sécurité routière · Ministère de l\'Intérieur',
        testid: 'barometre-infographie',
      },
      cards: [
        {
          icon: Heart,
          accent: '#cf2031',
          kicker: 'Mortalité totale',
          title: '306 tués en France',
          body:
            "Dont <strong>285 en métropole</strong> et 21 outre-mer. Soit <strong>+41 tués (+15 %)</strong> par rapport à avril 2025. C'est l'une des plus fortes hausses mensuelles depuis 2020.",
        },
        {
          icon: Bike,
          accent: '#cf2031',
          kicker: 'Deux-roues motorisé',
          title: '79 motards tués',
          body:
            "<strong>+27 morts</strong> en un mois vs avril 2025. C'est de très loin la catégorie d'usagers <strong>la plus durement frappée</strong>. Les blessés graves augmentent aussi de <strong>+8 %</strong>.",
        },
        {
          icon: Users,
          accent: '#f59e0b',
          kicker: 'Cyclistes',
          title: '28 tués',
          body:
            "<strong>+12 cyclistes tués</strong> par rapport à avril 2025. Une hausse également préoccupante alors que la pratique du vélo se développe en ville comme en milieu rural.",
        },
        {
          icon: TrendingUp,
          accent: '#7c3aed',
          kicker: 'Hors agglomération',
          title: '184 tués (+45)',
          body:
            "La mortalité hors agglomération <strong>explose</strong>. Routes secondaires, départementales : c'est là que la moto paie le plus lourd tribut.",
        },
      ],
      cardsCols: 2,
      callouts: [
        {
          icon: AlertTriangle,
          accent: '#cf2031',
          body:
            "<strong>Mise en perspective :</strong> les deux-roues motorisés ne représentent que <strong>~2 % du trafic routier</strong> en France, mais ils concentrent <strong>~25 % des morts</strong>. En avril 2026, ce ratio explose : <strong>1 mort sur 4</strong> sur les routes de métropole était à moto ou scooter.",
        },
      ],
    },
    {
      part: 'Partie 2 — Les âges qui souffrent',
      title: 'Une jeunesse fauchée — et pas seulement',
      intro:
        "La hausse touche <strong>toutes les tranches d'âge sauf une</strong>. Le profil des victimes interpelle.",
      compareCards: [
        {
          accent: '#cf2031',
          kicker: 'Mineurs (-18 ans)',
          title: '26 tués · +16 vs avril 2025',
          intro:
            "Une hausse <strong>extrême</strong> chez les adolescents : doublement de la mortalité sur un mois.",
          points: [
            { ok: false, text: 'Scooters 50 cm³ (cyclos AM) très exposés' },
            { ok: false, text: 'Trajet domicile-lycée souvent en cause' },
            { ok: false, text: 'Casque mal attaché ou non porté (statistique récurrente)' },
          ],
        },
        {
          accent: '#16a34a',
          kicker: '18-24 ans',
          title: '34 tués · -14 vs avril 2025',
          intro:
            "<strong>Seule tranche d'âge en baisse</strong>. Une lueur d'espoir qui montre que la prévention auprès des jeunes adultes commence à porter ses fruits.",
          points: [
            { ok: true, text: 'Probable effet des campagnes "alcool / vitesse / téléphone"' },
            { ok: true, text: "Formation initiale au permis B mieux suivie" },
            { ok: true, text: 'Mais reste une tranche structurellement à risque' },
          ],
        },
        {
          accent: '#cf2031',
          kicker: '25-64 ans',
          title: '142 tués · +29 vs avril 2025',
          intro:
            "La <strong>plus grosse contribution</strong> à la hausse globale en valeur absolue.",
          points: [
            { ok: false, text: 'Retour de la moto-loisir post-confinement' },
            { ok: false, text: "Surreprésentation des trajets domicile-travail" },
            { ok: false, text: "Effet 'expérience' parfois trompeur (surconfiance)" },
          ],
        },
        {
          accent: '#cf2031',
          kicker: 'Seniors (65+ ans)',
          title: '83 tués · +7 vs avril 2025',
          intro:
            "Hausse <strong>modérée</strong> mais préoccupante. Les seniors sont plus fragiles physiquement.",
          points: [
            { ok: false, text: 'Temps de réaction allongé (+0,3s en moyenne après 65 ans)' },
            { ok: false, text: 'Choc même léger = blessures graves (fragilité osseuse)' },
            { ok: false, text: 'Vue & ouïe : à faire vérifier tous les 2 ans' },
          ],
        },
      ],
    },
    {
      part: 'Partie 3 — Les 5 causes officielles',
      title: 'Pourquoi cette explosion ? Les 5 facteurs aggravants',
      intro:
        "La ministre Marie-Pierre Vedrenne a explicitement nommé <strong>5 comportements à risque</strong> dans son communiqué officiel. Tous sont des <strong>infractions sanctionnables</strong> au code de la route. Tous sont travaillés dans le programme ETM 2026.",
      cards: [
        {
          icon: Gauge,
          accent: '#cf2031',
          kicker: 'Cause n°1',
          title: 'Vitesse excessive',
          body:
            "<strong>1ère cause d'accident mortel à moto.</strong> À 90 km/h, ta distance de freinage est de <strong>~70 m</strong>. À 130 km/h, elle passe à <strong>~140 m</strong>. Une vitesse doublée, c'est une énergie de choc <strong>multipliée par 4</strong>.",
        },
        {
          icon: Wine,
          accent: '#cf2031',
          kicker: 'Cause n°2',
          title: 'Alcool',
          body:
            "Taux légal : <strong>0,5 g/L</strong> sang (0,2 g/L pour permis probatoire). Au-delà → délit, retrait 6 points, 4 500 € d'amende, suspension. <strong>30 % des morts moto</strong> ont un taux supérieur à la limite.",
        },
        {
          icon: Brain,
          accent: '#cf2031',
          kicker: 'Cause n°3',
          title: 'Stupéfiants',
          body:
            "Aucune tolérance : <strong>tout test positif = délit</strong>. Cannabis, cocaïne, héroïne, MDMA : 6 points retirés, 4 500 € d'amende, prison possible. Cumul alcool + stupéfiants = 9 000 €.",
        },
        {
          icon: Smartphone,
          accent: '#cf2031',
          kicker: 'Cause n°4',
          title: 'Téléphone au guidon',
          body:
            "<strong>Strictement interdit</strong> en moto, même à l'arrêt au feu rouge. Même tenir le téléphone = -3 points + 135 €. Les oreillettes/casques connectés sont <strong>aussi interdits</strong> depuis 2015.",
        },
        {
          icon: AlertTriangle,
          accent: '#cf2031',
          kicker: 'Cause n°5',
          title: 'Comportements déviants',
          body:
            "Wheeling, refus de priorité, dépassement dangereux, slalom entre files (interdit hors région parisienne en CRIT'Air), franchissement de ligne continue… <strong>Tout ce qui transgresse le code</strong>, mais aussi le simple bon sens.",
        },
        {
          icon: Eye,
          accent: '#cf2031',
          kicker: 'Bonus officiel',
          title: 'Défaut de visibilité',
          body:
            "<strong>Pour info :</strong> 35 % des accidents impliquant une moto ont pour cause un automobiliste qui n'a pas vu le motard (changement de file, sortie de stationnement). EPI visibles, feux de croisement allumés en permanence, anticipation = 3 réflexes vitaux.",
        },
      ],
      cardsCols: 3,
    },
    {
      part: 'Partie 4 — Que faire concrètement',
      title: '5 réflexes vitaux à appliquer dès maintenant',
      intro:
        "La ministre l'a dit : <em>« Derrière chaque accident évité, il y a une vie sauvée. »</em> Voici 5 actions <strong>concrètes</strong>, applicables dès ta prochaine sortie moto ou ta prochaine leçon d'auto-école.",
      cards: [
        {
          icon: ShieldCheck,
          accent: '#16a34a',
          kicker: 'Réflexe n°1',
          title: 'Vérifier ton équipement',
          body:
            "<strong>Casque homologué ECE 22-06</strong>, jacquette CE, gants CE certifiés, bottes montantes. <strong>Sans EPI complet = -1 point + 68 €</strong>. Mais surtout : sans EPI, un choc à 50 km/h = traumatisme garanti.",
        },
        {
          icon: Gauge,
          accent: '#16a34a',
          kicker: 'Réflexe n°2',
          title: 'Respecter les limites — VRAIMENT',
          body:
            "<strong>-10 km/h</strong> sur ta vitesse habituelle. C'est tout. Tu gagnes <strong>15 mètres de distance de freinage</strong> à 90 km/h. Soit la longueur de 3 voitures. Soit la vie d'un piéton.",
        },
        {
          icon: Eye,
          accent: '#16a34a',
          kicker: 'Réflexe n°3',
          title: 'Voir et être vu',
          body:
            "Feux allumés <strong>en permanence</strong>, regard 12 secondes devant, contrôle rétroviseurs <strong>toutes les 6-8 secondes</strong>. Anticipe les angles morts des voitures : si tu n'es pas dans leur rétro, tu es invisible.",
        },
        {
          icon: Heart,
          accent: '#16a34a',
          kicker: 'Réflexe n°4',
          title: 'Zéro alcool / zéro téléphone',
          body:
            "Pas de « petit verre » avant de prendre la moto. Pas de coup d'œil au GPS en roulant. Si tu dois lire un SMS : <strong>arrête-toi en sécurité</strong>. 30 secondes perdues ≠ ta vie.",
        },
        {
          icon: Calendar,
          accent: '#16a34a',
          kicker: 'Réflexe n°5',
          title: 'Te former en continu',
          body:
            "Le permis n'est pas une fin. <strong>Stage post-permis</strong> (7h, parfois remboursé par la mutuelle), <strong>journées circuit</strong>, vidéos pédagogiques officielles : continue à apprendre. Les motards expérimentés meurent autant que les jeunes.",
        },
        {
          icon: Target,
          accent: '#16a34a',
          kicker: 'Réflexe n°6',
          title: 'Continuer à te documenter',
          body:
            "Sur <strong>CODE 2 ROUES</strong>, retrouve nos <strong>dossiers pédagogiques moto</strong>, nos <strong>enquêtes sécurité routière</strong> et nos <strong>livrets plateau et circulation</strong>. <strong>100 % gratuit, sans pub, sans tracking.</strong> Plus tu maîtrises la théorie, plus tu anticipes les situations à risque en pratique.",
        },
      ],
      cardsCols: 3,
    },
  ],
  quiz: [
    {
      id: 'q1',
      question:
        "En avril 2026, combien d'usagers de deux-roues motorisé sont morts sur les routes de France métropolitaine ?",
      options: ['52', '79', '102', '143'],
      correctIndex: 1,
      explain:
        "79 motards et scootéristes ont perdu la vie en avril 2026 selon l'ONISR. C'est 27 morts de plus qu'en avril 2025 (52), soit une hausse de +52 % sur un seul mois.",
    },
    {
      id: 'q2',
      question:
        "Quelle est l'augmentation globale de la mortalité routière en France métropolitaine en avril 2026 vs avril 2025 ?",
      options: ['+5 %', '+10 %', '+15 %', '+25 %'],
      correctIndex: 2,
      explain:
        "Le baromètre officiel mentionne +15 % de mortalité en métropole (285 tués contre 248 un an plus tôt). Les deux-roues motorisés sont la catégorie la plus touchée.",
    },
    {
      id: 'q3',
      question:
        "Selon la ministre Marie-Pierre Vedrenne, lequel de ces comportements N'EST PAS cité parmi les principales causes d'accident ?",
      options: [
        'Vitesse excessive',
        'Alcool & stupéfiants',
        'Téléphone au volant',
        "Défaut d'entretien du véhicule",
      ],
      correctIndex: 3,
      explain:
        "Les 5 causes citées dans le communiqué officiel sont : vitesse, alcool, stupéfiants, téléphone au volant et comportements déviants. Le défaut d'entretien est une cause minoritaire (~5 % des accidents).",
    },
    {
      id: 'q4',
      question:
        "Quelle tranche d'âge est la SEULE à voir sa mortalité diminuer en avril 2026 ?",
      options: ['Moins de 18 ans', '18-24 ans', '25-64 ans', '65 ans et plus'],
      correctIndex: 1,
      explain:
        "Les 18-24 ans enregistrent -14 tués (34 morts vs 48 en avril 2025). Probablement l'effet positif des campagnes de prévention ciblées sur les jeunes conducteurs et de la formation initiale au permis B.",
    },
    {
      id: 'q5',
      question:
        "Pour un motard, à 90 km/h, quelle est environ la distance totale d'arrêt sur chaussée sèche ?",
      options: ['Environ 40 m', 'Environ 70 m', 'Environ 100 m', 'Environ 140 m'],
      correctIndex: 1,
      explain:
        "Distance de réaction (~25 m) + distance de freinage (~45 m) = environ 70 m à 90 km/h. À 130 km/h, on monte à ~140 m. La vitesse est la 1ère cause de mortalité moto.",
    },
    {
      id: 'q6',
      question:
        "Les usagers de deux-roues motorisé représentent ~2 % du trafic routier. Quelle part des morts représentent-ils en avril 2026 ?",
      options: ['~5 %', '~15 %', '~25 %', '~50 %'],
      correctIndex: 2,
      explain:
        "Avec 79 tués sur 285 en métropole, les 2RM représentent ~28 % de la mortalité routière du mois, alors qu'ils ne pèsent que ~2 % du trafic. Le risque relatif d'un motard est ~20 fois supérieur à celui d'un automobiliste.",
    },
  ],
  ctaFinal: {
    kicker: 'Passe à l\'action',
    title: 'Réviser, c\'est protéger ta vie',
    body:
      "Chaque question de l'examen ETM 2026 a été conçue pour identifier les situations à risque <em>avant</em> qu'elles ne deviennent un accident. Notre base 100 % gratuite est mise à jour en continu selon les données officielles de la Sécurité Routière.",
    primary: { label: "Explorer un autre dossier magazine", to: '/magazine' },
    secondary: { label: "Consulter le livret plateau", to: '/livret-plateau' },
  },
};
